import React, { Suspense, lazy } from 'react';
import { useCallback, useEffect, useRef, useState } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";

import PageHeader from "./PageHeader";
import PageBody from "./PageBody";
import PageFooter from "./PageFooter";
import EntityFetcher from "@/core/EntityFetcher";
import { ProductContext } from "@/core/providers/ProductContext";

const ProductsPage = lazy(() => import("./products/ProductsPage"));
const DocumentCenterPage = lazy(() => import("./documents/DocumentCenterPage"));
const ImageGalleryPage = lazy(() => import("./gallery/ImageGalleryPage"));
const VideoGalleryPage = lazy(() => import("./gallery/VideoGalleryPage"));
const VideoGalleryDetailPage = lazy(() => import("./gallery/VideoGalleryDetailPage"));
const BlogDetailPage = lazy(() => import("./blog/BlogDetailPage"));
const SearchPage = lazy(() => import("./search/SearchPage"));
const PDFViewer = lazy(() => import("./pdfViewer/PdfViewer"));
const FormassemblyDemoPage = lazy(() => import("./forms/FormassemblyDemoPage"));

import formRoutes from "./forms";
import { BreadcrumbContext } from "@/core/providers/BreadcrumbContext";

const routes = [
  { path: "/", component: EntityFetcher },
  { path: "/404", component: EntityFetcher },
  { path: "/products/folding-doors", component: EntityFetcher },
  { path: "/products/sliding-doors", component: EntityFetcher },
  { path: "/products/swing-doors", component: EntityFetcher },
  { path: "/products/screens", component: EntityFetcher },
  { path: "/products/:slug+", component: ProductsPage },
  { path: "/support/technical-documents", component: DocumentCenterPage },
  { path: "/image-gallery/category/:category", component: ImageGalleryPage },
  { path: "/image-gallery", component: ImageGalleryPage },
  { path: "/video-gallery/category/:category", component: VideoGalleryPage },
  { path: "/video-gallery", component: VideoGalleryPage },
  { path: "/video-gallery/:slug", component: VideoGalleryDetailPage },
  { path: "/blog/*", component: BlogDetailPage },
  { path: "/learn/featured-projects/*", component: BlogDetailPage },
  { path: "/news/*", component: BlogDetailPage },
  { path: "/search", component: SearchPage },
  { path: "/search/content/:type?/:term?", component: SearchPage },
  { path: "/search/:term?", component: SearchPage },
  { path: "/pdfViewer", component: PDFViewer },
  { path: "/formassembly-demo", component: FormassemblyDemoPage },
].concat(formRoutes);


const previewRoutes = [
  { path: "/preview/products/folding-doors", component: EntityFetcher },
  { path: "/preview/products/sliding-doors", component: EntityFetcher },
  { path: "/preview/products/swing-doors", component: EntityFetcher },
  { path: "/preview/products/screens", component: EntityFetcher },
  { path: "/preview/products/:slug*", component: ProductsPage },
  { path: "/preview/news/*", component: BlogDetailPage },
  { path: "/preview/blog/*", component: BlogDetailPage },
  { path: "/preview/learn/featured-projects/*", component: BlogDetailPage },
  { path: "/preview/*", component: EntityFetcher },
]

export default function PageLayout() {
  let location = useLocation();
  const previousLocation = useRef();
  const [showHeaderFooter, setShowHeaderFooter] = useState(true);

  // This is the "app-wide" variable that hold the value for the last product page visited. 
  // Normally it only comes up on product pages themselves, but this is up here
  // and uses a context so that any part of the site is able to read and react to there being
  // a product "loaded".
  const [product, setProduct] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState(null);


  const onRouteChange = useCallback(() => {

    // push analytics page change event
    if (location.pathname !== '/support/technical-documents' && !location.pathname.includes('/search/')) {

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'virtualPageview',               // name of the event. In this case, it always must be virtualPageview
        page_url: location.pathname,  // the URI of the page, including query strings and fragment identifiers
        page_title: document.title,                 // friendly name of the page
      }); 

    }

    // scroll back to top o f page
    window.scrollTo(0, 0);

    // check if pdfview to exclude header/footer
    const excludedRoutes = ["/pdfViewer"]; // Add other routes where you want to exclude the header and footer
    setShowHeaderFooter(!excludedRoutes.includes(location.pathname));

  }, [location]);

  useEffect(() => {
    // look for actual page changes, not query changes (i.e. doc center)
    if (location.pathname !== previousLocation.current) {
      onRouteChange();
    }
    previousLocation.current = location.pathname;
  }, [location, onRouteChange]);

  return (
    <>
     <BreadcrumbContext.Provider value={{breadcrumbs, setBreadcrumbs}}>
      <ProductContext.Provider value={{ product, setProduct }}>
       
          {showHeaderFooter && <PageHeader />}
          <PageBody>

            <Suspense fallback={<></>}>

              <Switch>

                {previewRoutes.map((r) => {
                  const Component = r.component;
                  return (
                    <Route key={r.path} exact path={r.path}>
                      <Component preview={true} />
                    </Route>
                  );
                })}


                {routes.map((r) => {
                  const Component = r.component;
                  return (
                    <Route key={r.path} exact path={r.path}>
                      <Component />
                    </Route>
                  );
                })}

                <Route exact path="/products">
                  <Redirect to="/products/folding-doors" />
                </Route>

                <Route exact path="/professionals">
                  <Redirect to="/professionals/architects-designers-builders" />
                </Route>

                <Route path="*">
                  <EntityFetcher />
                </Route>
              </Switch>

            </Suspense>

          </PageBody>
          {showHeaderFooter && <PageFooter />}
      
        </ProductContext.Provider>
      </BreadcrumbContext.Provider>


    </>
  );
}
